// ─────────────────────────────────────────────────────────────────────
// Constants & helpers
// ─────────────────────────────────────────────────────────────────────
export const FONT_SANS = '"Inter Tight", "Söhne", -apple-system, system-ui, sans-serif';
export const FONT_MONO = '"IBM Plex Mono", "JetBrains Mono", ui-monospace, Menlo, monospace';
export const SQRT3 = Math.sqrt(3);

// Theme tokens map to CSS vars (so the visual primitives can read v(...) too)
export function v(name: string): string { return `var(--${name})`; }

export type Axis = 'col' | 'nwse' | 'nesw';

export interface HexCellGeom {
  col: number;
  row: number;
  x: number;
  y: number;
  n: number;
  idx: number;
}

// Hex geometry — flat-top
export function hexPoints(cx: number, cy: number, R: number): string {
  const pts: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i);
    pts.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
  }
  return pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
}

// Hexagon-shaped hex grid: counts go N, N+1, ..., 2N-1, ..., N+1, N — i.e.,
// size 3 → [3,4,5,4,3] (19 cells), size 4 → [4,5,6,7,6,5,4] (37 cells).
export function gridCounts(size: number): number[] {
  const result: number[] = [];
  for (let i = 0; i < size; i++) result.push(size + i);
  for (let i = size - 2; i >= 0; i--) result.push(size + i);
  return result;
}

// Total cell count for a hex grid of side N: 3N² - 3N + 1.
export function cellCountForSize(size: number): number {
  return 3 * size * size - 3 * size + 1;
}

// Inverse: derive size from a cell count produced by `cellCountForSize`.
export function sizeFromCellCount(n: number): number {
  return Math.round((3 + Math.sqrt(12 * n - 3)) / 6);
}

// Flat-top hex grid, size-parameterized. Default size=3 keeps existing
// callers (Easy/Medium/Hard) on the legacy 19-cell layout.
export function buildHexCells(R: number, gap = 4, size = 3): HexCellGeom[] {
  const counts = gridCounts(size);
  const xStride = 1.5 * R + gap;
  const yStride = SQRT3 * R + gap;
  const center = (counts.length - 1) / 2;
  const cells: HexCellGeom[] = [];
  for (let col = 0; col < counts.length; col++) {
    const n = counts[col];
    const x = (col - center) * xStride;
    const startY = -((n - 1) / 2) * yStride;
    for (let i = 0; i < n; i++) {
      cells.push({ col, row: i, x, y: startY + i * yStride, n, idx: cells.length });
    }
  }
  return cells;
}

// Line definitions — three principal hex axes (col, nwse, nesw), each with
// (2*size - 1) lines. Lines are derived from cell positions in unit-grid
// coordinates: nwse lines share `y - 0.5x`, nesw lines share `y + 0.5x`.
function computeLines(size: number): Record<Axis, number[][]> {
  const counts = gridCounts(size);
  const center = (counts.length - 1) / 2;
  type CellPos = { col: number; row: number; xUnit: number; yUnit: number; idx: number };
  const cells: CellPos[] = [];
  for (let col = 0; col < counts.length; col++) {
    const n = counts[col];
    const xUnit = col - center;
    const startY = -((n - 1) / 2);
    for (let r = 0; r < n; r++) {
      cells.push({ col, row: r, xUnit, yUnit: startY + r, idx: cells.length });
    }
  }

  const cols: number[][] = Array.from({ length: counts.length }, () => []);
  for (const c of cells) cols[c.col].push(c.idx);

  const groupBy = (key: (c: CellPos) => number): number[][] => {
    const map = new Map<number, number[]>();
    for (const c of cells) {
      const k = key(c);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(c.idx);
    }
    const keys = [...map.keys()].sort((a, b) => a - b);
    const lines = keys.map(k => map.get(k)!);
    lines.forEach(line => line.sort((a, b) => cells[a].xUnit - cells[b].xUnit));
    return lines;
  };

  return {
    col: cols,
    nwse: groupBy(c => c.yUnit - 0.5 * c.xUnit),
    nesw: groupBy(c => c.yUnit + 0.5 * c.xUnit),
  };
}

const LINES_CACHE = new Map<number, Record<Axis, number[][]>>();
export function getLines(size: number): Record<Axis, number[][]> {
  let cached = LINES_CACHE.get(size);
  if (!cached) {
    cached = computeLines(size);
    LINES_CACHE.set(size, cached);
  }
  return cached;
}

// Convenience: derive the lines for a puzzle by its cell count. Most call
// sites have `cells: Cell[]` available and don't carry a size around.
export function linesForCells(cellCount: number): Record<Axis, number[][]> {
  return getLines(sizeFromCellCount(cellCount));
}

// Anchor candidates per size — top, middle, and bottom of each column. Used
// by the generator to bias lock placement toward visually balanced cells.
export function getAnchorCandidates(size: number): number[] {
  const counts = gridCounts(size);
  const anchors: number[] = [];
  let offset = 0;
  for (const n of counts) {
    anchors.push(offset);
    if (n > 2) anchors.push(offset + Math.floor(n / 2));
    if (n > 1) anchors.push(offset + n - 1);
    offset += n;
  }
  return anchors;
}

// Legacy size-3 export retained for any straggler reads — prefer
// linesForCells / getLines at new call sites.
export const LINES = getLines(3);
